/**
 * The ONE project scope (card 1.1) — what the toolbar switcher picks and what
 * API Requests, Boards, Documents and the connection lists follow (cards 1.2,
 * 2.1, 3.x, 4.x, 5.x wire themselves to it; this module only holds the state).
 *
 *   activeProjectId  'all' | 'none' | <projectId>, persisted per device
 *   activeProject    the resolved project, or null ('all' / 'none' / not known yet)
 *   status           'ready' | 'pending' (see projectScopeFilter.resolveActiveProject)
 *
 * The known projects come from `setKnownProjects` (projectScopeLoader reads the
 * personal bucket plus one bucket per team ON THE ROSTER). When the active
 * project turns out to be gone — deleted, or the user left its team — the
 * scope falls back to 'all' and that is persisted. While it is only pending
 * (roster not loaded) the stored id is kept and nothing matches.
 */
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { ALL_PROJECTS, normalizeScopeId, resolveActiveProject } from './projectScopeFilter';

export const PROJECT_SCOPE_STORAGE_KEY = 'apiboo.projectScope.v1';

function readPersisted() {
  try {
    return normalizeScopeId(window.localStorage.getItem(PROJECT_SCOPE_STORAGE_KEY));
  } catch {
    return ALL_PROJECTS;
  }
}

function persist(id) {
  try {
    window.localStorage.setItem(PROJECT_SCOPE_STORAGE_KEY, id);
  } catch {
    // Private mode / storage full: the scope still works for this session.
  }
}

const EMPTY_KNOWN = Object.freeze({ loaded: false, projects: [], links: [], docSpaces: [], docSpaceMembers: [], rosterKnown: false, rosterTeamIds: [] });

/** The derived fields for `id` against `known`, applying the 'gone' → 'all' fallback. */
function derive(id, known) {
  const r = resolveActiveProject(id, known);
  if (r.status === 'gone') {
    persist(ALL_PROJECTS);
    return { activeProjectId: ALL_PROJECTS, activeProject: null, status: 'ready' };
  }
  return r;
}

export const useProjectScopeStore = create((set, get) => ({
  ...resolveActiveProject(readPersisted(), EMPTY_KNOWN),
  known: EMPTY_KNOWN,

  /**
   * Picks the scope ('all', 'none' or a project id) and persists it. An id the
   * known list does not have YET stays pending instead of falling back: it was
   * just picked or just created (the migration activates the project it made a
   * moment ago), and only the next `setKnownProjects` can call it gone.
   */
  setActiveProject(value) {
    const id = normalizeScopeId(value);
    persist(id);
    const r = resolveActiveProject(id, get().known);
    set(r.status === 'gone' ? { activeProjectId: id, activeProject: null, status: 'pending' } : r);
  },

  /**
   * What the loader found: `{ projects, rosterKnown, rosterTeamIds }`.
   * Re-resolves the active project (and falls back when it is gone).
   */
  setKnownProjects({ projects = [], links = [], docSpaces = [], docSpaceMembers = [], rosterKnown = false, rosterTeamIds = [] } = {}) {
    const known = {
      loaded: true,
      projects: [...projects],
      links: [...links],
      docSpaces: [...docSpaces],
      docSpaceMembers: [...docSpaceMembers],
      rosterKnown: Boolean(rosterKnown),
      rosterTeamIds: rosterTeamIds.map(String),
    };
    set({ known, ...derive(get().activeProjectId, known) });
  },

  /** Back to the persisted value with nothing known (sign-out, tests). */
  reset() {
    set({ known: EMPTY_KNOWN, ...resolveActiveProject(readPersisted(), EMPTY_KNOWN) });
  },
}));

const pickScope = (s) => ({ activeProjectId: s.activeProjectId, activeProject: s.activeProject, status: s.status });

/** `useProjectScope()` → `{ activeProjectId, activeProject, status }`; or pass a selector. */
export function useProjectScope(selector) {
  return useProjectScopeStore(useShallow(selector || pickScope));
}

export const setActiveProject = (id) => useProjectScopeStore.getState().setActiveProject(id);
export const getProjectScope = () => pickScope(useProjectScopeStore.getState());
